import { useMemo, useState } from 'react'
import { Link as InertiaLink } from '@inertiajs/react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import dayjs from 'dayjs'

import { DataTableRoot, DataTableContent, SortableHeader } from '@components/molecules/DataTable'
import { PageLayout } from '@components/layouts/PageLayout'
import Badge from '@components/atoms/Badge'
import { Progress } from '@atoms/Progress'
import EmptyState from '@components/molecules/EmptyState'
import LoadingWrapper from '@components/molecules/LoadingWrapper'
import { PageHeaderNav, type PageTabs } from '@molecules/PageHeaderNav'
import {
  CallsGroupFilter,
  ShowAllGroupsLink,
  selectedCallsGroup,
  showCallsGroups,
  type CallsGroup,
} from '@components/calls/CallsGroupFilter'
import { scopedTranslation } from '@frontend/utils/I18n'
import type { CallingSessionsIndexQueryType } from '@frontend/graphql/queries/calling_sessions'
import { DialogProvider, type DialogRegistry } from '@components/DialogProvider'
import { CallingSessionActions } from '../features/calling-session-actions/CallingSessionActions'
import { ArchiveCallingSessionDialog } from '../features/calling-session-actions/ArchiveCallingSessionDialog'
import {
  GroupPageHeader,
  type GroupPageHeaderProps,
} from '@frontend/contexts/supporters-management/features/group-page/GroupPageHeader'

const t = scopedTranslation('calling_sessions_index')
const tGroupFilter = scopedTranslation('shared.group_filter')
const NO_GROUPS: CallsGroup[] = []

const callingSessionsIndexRegistry: DialogRegistry = {
  archiveCallingSession: ArchiveCallingSessionDialog,
}

type CallingSession = NonNullable<CallingSessionsIndexQueryType['callingSessions']>['nodes'][number]

type CallingSessionsIndexProps = CallingSessionsIndexQueryType & {
  groupHeader?: GroupPageHeaderProps | null
  pageTabs?: PageTabs | null
  groups?: CallsGroup[]
  filterGroupIds?: string[]
}

const CallingSessionsIndex = ({
  callingSessions,
  groupHeader,
  pageTabs,
  groups = NO_GROUPS,
  filterGroupIds = [],
}: CallingSessionsIndexProps) => {
  const sessions = callingSessions.nodes
  const groupNames = useMemo(() => new Map(groups.map((group) => [group.id, group.name])), [groups])
  const [sorting, setSorting] = useState<SortingState>([])
  const [filtering, setFiltering] = useState(false)
  const showGroups = !groupHeader && showCallsGroups(groups, filterGroupIds)
  const filtered = filterGroupIds.length > 0
  const selectedGroup = selectedCallsGroup(groups, filterGroupIds)

  const columns: Array<ColumnDef<CallingSession>> = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => <SortableHeader column={column}>{t('table.headers.session')}</SortableHeader>,
        cell: ({ row }) => (
          <div>
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
              <InertiaLink
                href={`/calling_sessions/${row.original.id}`}
                className="tw:font-medium tw:text-primary-700 tw:border-b tw:border-transparent tw:hover:text-primary-900 tw:hover:border-current tw:transition-colors tw:duration-200"
              >
                {row.original.name}
              </InertiaLink>
              {row.original.status === 'in_progress' && <Badge color="success">{t('status.in_progress')}</Badge>}
            </div>
            <div className="tw:text-sm tw:text-gray-500">{row.original.callList.name}</div>
          </div>
        ),
      },
      ...(!showGroups
        ? []
        : [
            {
              id: 'group',
              accessorFn: (session: CallingSession) => groupNames.get(session.actionGroupId) ?? '',
              header: ({ column }) => <SortableHeader column={column}>{tGroupFilter('group')}</SortableHeader>,
            } satisfies ColumnDef<CallingSession>,
          ]),
      {
        accessorKey: 'completion',
        accessorFn: (session) =>
          session.totalSupportersCount > 0 ? session.completedSupportersCount / session.totalSupportersCount : 0,
        header: ({ column }) => <SortableHeader column={column}>{t('table.headers.completion')}</SortableHeader>,
        cell: ({ row }) => {
          const { completedSupportersCount, totalSupportersCount } = row.original
          const percentage =
            totalSupportersCount > 0 ? Math.round((completedSupportersCount / totalSupportersCount) * 100) : 0
          return (
            <div className="tw:flex tw:w-32 tw:flex-col tw:gap-1">
              <Progress
                size="compact"
                value={percentage}
                aria-label={t('progress_label', { name: row.original.name ?? '' })}
              />
              <span className="tw:text-right tw:text-xs tw:tabular-nums tw:text-gray-500">
                {t('progress', { completed: completedSupportersCount, total: totalSupportersCount })}
              </span>
            </div>
          )
        },
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <SortableHeader column={column}>{t('table.headers.created')}</SortableHeader>,
        cell: ({ row }) => {
          try {
            const createdAt: string | null | undefined = row.getValue('createdAt')
            const date = dayjs(createdAt)
            if (!date.isValid()) return null
            return <div>{date.format('D MMM YYYY')}</div>
          } catch {
            return null
          }
        },
      },
      {
        id: 'actions',
        header: () => <div className="tw:font-bold">{t('table.headers.actions')}</div>,
        cell: ({ row }) => <CallingSessionActions session={row.original} />,
        enableSorting: false,
        meta: { alignRight: true },
      },
    ],
    [showGroups, groupNames]
  )

  const table = useReactTable({
    data: sessions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  })

  return (
    <DialogProvider registry={callingSessionsIndexRegistry}>
      <PageLayout.Root>
        {groupHeader ? (
          <GroupPageHeader {...groupHeader} linkComponent={InertiaLink} />
        ) : (
          <PageLayout.Header
            icon="phone-call-01"
            heading={pageTabs?.label ?? t('page_title')}
            nav={
              pageTabs && (
                <PageHeaderNav ariaLabel={pageTabs.label} items={pageTabs.items} linkComponent={InertiaLink} />
              )
            }
          />
        )}
        <PageLayout.Body>
          {showGroups && <CallsGroupFilter groups={groups} groupIds={filterGroupIds} onLoadingChange={setFiltering} />}
          <LoadingWrapper loading={filtering}>
            {sessions.length === 0 ? (
              <EmptyState
                headline={
                  selectedGroup
                    ? t('empty_state.no_matches_in', { group: selectedGroup.name })
                    : t(filtered ? 'empty_state.no_matches' : 'empty_state.title')
                }
                subtext={t('empty_state.description')}
                iconType="phone-call-01"
              >
                {filtered && <ShowAllGroupsLink />}
              </EmptyState>
            ) : (
              <DataTableRoot table={table}>
                <DataTableContent />
              </DataTableRoot>
            )}
          </LoadingWrapper>
        </PageLayout.Body>
      </PageLayout.Root>
    </DialogProvider>
  )
}

export default CallingSessionsIndex
